import { app } from "api";
import { useMutation, useQuery, useQueryCache } from "react-query";
import Discount, { getDiscountValues } from "classes/discount";

export function getTests() {
  return app
    .firestore()
    .collection("tests")
    .get()
    .then((tests) => tests.docs.map((i) => i.data()));
}

export function getLabTests() {
  const uid = app.auth().currentUser!.uid;
  return app
    .firestore()
    .collection("labs")
    .doc(uid)
    .collection("labTests")
    .get()
    .then((tests) => tests.docs.map((i) => i.data()));
}

export function getLabTestInfo(testId: string) {
  const uid = app.auth().currentUser!.uid;
  return app
    .firestore()
    .collection("labs")
    .doc(uid)
    .collection("labTests")
    .doc(testId)
    .get()
    .then((test) => test.data());
}

export function addTestToLab(test: any) {
  const uid = app.auth().currentUser!.uid;
  const ref = app
    .firestore()
    .collection("labs")
    .doc(uid)
    .collection("labTests")
    .doc();
  return ref.set({
    ...test,
    id: ref.id,
    labId: uid,
    fee: parseFloat(test.fee),
    createdAt: new Date().getTime(),
    enabled: true,
  });
}

function getDiscountRef({
  testId,
  discountId,
}: {
  testId: string;
  discountId?: string;
}) {
  const uid = app.auth().currentUser!.uid;
  if (!discountId) {
    discountId = undefined;
  }
  return app
    .firestore()
    .collection("labs")
    .doc(uid)
    .collection("labTests")
    .doc(testId)
    .collection("discounts")
    .doc(discountId);
}

export function getTestDiscounts(
  testId: string
): Promise<Array<Discount>> | undefined {
  if (!testId) {
    return;
  }
  const uid = app.auth().currentUser!.uid;
  return app
    .firestore()
    .collection("labs")
    .doc(uid)
    .collection("labTests")
    .doc(testId)
    .collection("discounts")
    .where("endTime", ">", new Date().getTime())
    .orderBy("endTime")
    .get()
    .then((discounts) =>
      discounts.docs.map((i) => new Discount({ discountData: i.data() }))
    );
}

export async function createOrUpdateDiscount(discount: Discount): Promise<any> {
  const uid = app.auth().currentUser!.uid;
  let discountRef = getDiscountRef({
    testId: discount.testId,
    discountId: discount.id,
  });
  if (!discount.id) {
    discount.labId = uid;
    discount.id = discountRef.id;
  }

  await discountRef.set({ ...getDiscountValues(discount) }, { merge: true });
  return discount;
}

export async function deleteDiscount(discount: Discount): Promise<any> {
  let discountRef = getDiscountRef({
    testId: discount.testId,
    discountId: discount.id,
  });

  await discountRef.delete();
  return discount;
}

export function useTests() {
  return useQuery("tests", getTests);
}

export function useLabTests() {
  return useQuery("lab-tests", getLabTests);
}

export function useLabTest(testId: string) {
  return useQuery(`lab-tests-${testId}`, () => getLabTestInfo(testId));
}

export function useAddLabTest() {
  const queryCache = useQueryCache();

  return useMutation((test) => addTestToLab(test), {
    onSuccess: () => {
      queryCache.invalidateQueries("lab-tests");
    },
  });
}

export function useTestDiscounts(testId: string) {
  return useQuery(`test-discounts-${testId}`, () => getTestDiscounts(testId));
}

export function useCreateTestDiscount() {
  const queryCache = useQueryCache();
  return useMutation((discount: Discount) => createOrUpdateDiscount(discount), {
    onSuccess: (discount) => {
      queryCache.invalidateQueries(`test-discounts-${discount.testId}`);
    },
  });
}

export function useDeleteTestDiscount() {
  const queryCache = useQueryCache();
  return useMutation((discount: Discount) => deleteDiscount(discount), {
    onSuccess: (discount: any) => {
      let testDiscounts: any = queryCache.getQueryData(
        `test-discounts-${discount.testId}`
      );

      testDiscounts = testDiscounts.filter(
        (dis: Discount) => dis.id !== discount.id
      );

      queryCache.setQueryData(
        `test-discounts-${discount.testId}`,
        testDiscounts
      );
    },
  });
}

export function getLabRef() {
  const uid = app.auth().currentUser!.uid;
  return app.firestore().collection("labs").doc(uid);
}

export function useDeleteLabTest() {
  const queryCache = useQueryCache();
  const uid = app.auth().currentUser!.uid;

  return useMutation(
    (testId) =>
      app
        .firestore()
        .collection("labs")
        .doc(uid)
        .collection("labTests")
        .doc(testId)
        .delete(),
    {
      onSuccess: (data, testId) => {
        queryCache.setQueryData("lab-tests", (old: any) =>
          old.filter((i: any) => i.id !== testId)
        );

        queryCache.invalidateQueries("lab-tests");
      },
    }
  );
}

export function useEditLabTest() {
  const queryCache = useQueryCache();

  return useMutation(
    ({ fee, testId }: any) =>
      getLabRef()
        .collection("labTests")
        .doc(testId)
        .update({ fee: parseFloat(fee), updatedAt: new Date().getTime() }),
    {
      onSuccess: (res, variables) => {
        queryCache.setQueryData("lab-tests", (old: any) =>
          old.map((i: any) => {
            if (i.id === variables.testId) {
              i.fee = variables.fee;
            }
            return i;
          })
        );
        queryCache.invalidateQueries("lab-tests");
      },
    }
  );
}
